<script setup>
import { computed, inject, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import Help from './Help.vue';
import Step from './Step.vue';
import TestIdea from './Idea.vue';
import Media from './Media.vue';
import Prize from './Prize.vue';
import Share from './Share.vue';
import Stepper from './Stepper.vue';
import Preview from './Preview.vue';
import Publish from './Publish.vue';
import Duration from './Duration.vue';
import Overview from './Overview.vue';
import Question from './Question.vue';
import MediaArticle from './Article.vue';
import Innovation from './Innovation.vue';
import Categories from './Categories.vue';
import Assumptions from './Assumptions.vue';
import CaptionFormat from './CaptionFormat.vue';
import { useChallengeCreationFlow } from '/app/composables/challenge-create-flow';
import { useCategories } from '/app/composables/categories';
import { breadcrumbs } from '/app/utils/categories';

const {
    completedCategory, completedImage, completedDuration, completedPrize,
    completedCaptionFormat, completedTitle, completedAssumptions,
    completedIdea, completedInnovation, hasSlug,
} = useChallengeCreationFlow();
const { categories } = useCategories();

const builder = inject('builder');
const stepper = useTemplateRef('stepper');
</script>

<template>
    <Stepper ref="stepper">
        <template #left>
            <div class="order-2 lg:order-1 col-span-12 sm:col-span-5 lg:col-span-4 xl:col-span-3 mt-5 lg:mt-0">
                <div class="challenge-section-heading is-center">
                    <p class="challenge-section-heading__title">
                        <i>Your Question Preview</i>
                    </p>
                    <small class="challenge-section-heading__description">
                        <i v-tooltip="(breadcrumbs({ children: categories }, form.fields.challenge.platform_category).map(c => c.name).join(' > '))">
                            In progress
                        </i>
                    </small>
                </div>
                <Preview :challenge="$parent.fields.challenge" />
            </div>
        </template>
        <Step class="col-span-12" title="Category" :complete="completedCategory" :disabled="hasSlug" hide-actions>
            <Categories
                v-model="$parent.fields.challenge.platform_category"
                v-model:categoryTree="builder.categoryTree"
                @change="() => stepper.number(2)"
            />
        </Step>
        <Step class="col-span-12 order-1 sm:order-2 sm:col-span-7 lg:col-span-8 xl:col-span-9" title="Your Idea" :disabled="hasSlug" :complete="completedIdea" show-left>
            <TestIdea />
        </Step>
        <Step class="col-span-12 order-1 sm:order-2 sm:col-span-7 lg:col-span-8 xl:col-span-9" title="Question &amp; Description" :disabled="hasSlug" :complete="completedTitle" show-left>
            <Question />
        </Step>
        <Step class="col-span-12 order-1 sm:order-2 sm:col-span-7 lg:col-span-8 xl:col-span-9" title="Assumptions" :disabled="hasSlug || !completedTitle" :complete="completedAssumptions" show-left>
            <Assumptions :challenge="$parent.fields.challenge" />
        </Step>
        <Step class="col-span-12 order-1 sm:order-2 sm:col-span-7 lg:col-span-8 xl:col-span-9" title="Image" :disabled="hasSlug || !completedTitle" :complete="completedImage" show-left>
            <Media />
        </Step>
        <Step class="col-span-12 order-1 sm:order-2 sm:col-span-7 lg:col-span-8 xl:col-span-9" title="Innovation Scale" :disabled="hasSlug || !completedTitle" :complete="completedInnovation" show-left>
            <Innovation />
        </Step>
        <Step class="col-span-12 order-1 sm:order-2 sm:col-span-7 lg:col-span-8 xl:col-span-9" title="Duration" :disabled="hasSlug || !completedTitle" :complete="completedDuration" show-left>
            <Duration />
        </Step>
        <Step class="col-span-12 order-1 sm:order-2 sm:col-span-7 lg:col-span-8 xl:col-span-9" title="Money &amp; Prizes" :disabled="hasSlug || !completedTitle" :complete="completedPrize" show-left>
            <Prize />
        </Step>
        <Step class="col-span-12 order-1 sm:order-2 sm:col-span-7 lg:col-span-8 xl:col-span-9" title="Caption Format" :disabled="hasSlug || !completedTitle" :complete="completedCaptionFormat" show-left>
            <CaptionFormat />
        </Step>
        <Step class="col-span-12" title="Preview, Edit &amp; Publish" :disabled="hasSlug || !completedTitle" :complete="hasSlug" activator>
            <div class="grid grid-cols-12 gap-4">
                <div class="col-span-12 sm:col-span-8">
                    <Publish />
                </div>

                <div class="col-span-12 sm:col-span-4">
                    <Overview />
                </div>
            </div>
        </Step>
        <Step :disabled="!hasSlug" title="Share" hide-actions>
            <Share />
        </Step>
    </Stepper>
</template>
